"use client";

import posthog from "posthog-js";
import type { CaptureResult } from "posthog-js";
import {
  PRODUCT_EVENT_NAMES,
  TELEMETRY_EVENT_NAMES,
} from "@/lib/posthog/product-event-names";

let initialized = false;
let captureEnabled = false;
let replayEnabled = false;

export const POSTHOG_READY_EVENT = "deepface:posthog-ready";

type PostHogConsentOptions = {
  analytics_tracking: boolean;
  session_replay: boolean;
};

function readConfig() {
  const token = process.env.NEXT_PUBLIC_POSTHOG_TOKEN?.trim();
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST?.trim();

  if (!token || !host) {
    return null;
  }

  return { token, host };
}

function applyPostHogConsent(options: PostHogConsentOptions) {
  captureEnabled = Boolean(options.analytics_tracking);
  replayEnabled = captureEnabled && Boolean(options.session_replay);

  if (captureEnabled) {
    posthog.opt_in_capturing?.();
  } else {
    posthog.opt_out_capturing?.();
  }

  if (replayEnabled) {
    posthog.startSessionRecording?.();
  } else {
    posthog.stopSessionRecording?.();
  }

  if (captureEnabled && typeof window !== "undefined") {
    window.dispatchEvent(new Event(POSTHOG_READY_EVENT));
  }
}

function sanitizeUrlPropertyValue(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  try {
    const parsed = new URL(value);
    parsed.search = "";
    parsed.hash = "";
    return parsed.toString();
  } catch {
    return null;
  }
}

const SAFE_EVENT_PROPERTY_NAMES = new Set([
  "$current_url",
  "$geoip_disable",
  "$pathname",
  "$process_person_profile",
  "$session_id",
  "$window_id",
  "location",
  "marketing_referral_campaign",
  "marketing_referral_link_name",
  "marketing_referral_slug",
  "marketing_referral_source",
  "method",
  "path",
  "reason",
  "result",
  "session_replay",
  "source_path",
  "surface",
  "target",
  "token",
  "distinct_id",
]);

function sanitizePostHogEvent(event: CaptureResult | null) {
  if (!event?.event || !event.properties) {
    return event;
  }

  // Replay is separately consented, masked, blocked on sensitive surfaces, and
  // retained for 30 days. Its snapshot payload cannot use the product-event
  // allowlist without breaking recording delivery.
  if (event.event === "$snapshot") {
    event.properties.$process_person_profile = false;
    event.properties.$geoip_disable = true;
    return event;
  }

  const isProductEvent = PRODUCT_EVENT_NAMES.has(event.event);
  const isTelemetryEvent = TELEMETRY_EVENT_NAMES.has(event.event);
  const isPageEvent = event.event === "$pageview" || event.event === "$pageleave";
  if (!isProductEvent && !isTelemetryEvent && !isPageEvent) {
    return null;
  }

  for (const propertyName of Object.keys(event.properties)) {
    if (!SAFE_EVENT_PROPERTY_NAMES.has(propertyName)) {
      delete event.properties[propertyName];
    }
  }

  if (isProductEvent || isTelemetryEvent) {
    delete event.properties.$current_url;
    delete event.properties.$pathname;
  } else {
    const sanitizedUrl = sanitizeUrlPropertyValue(event.properties.$current_url);
    if (sanitizedUrl) {
      event.properties.$current_url = sanitizedUrl;
    } else {
      delete event.properties.$current_url;
    }
    const pathname = event.properties.$pathname;
    if (typeof pathname === "string" && pathname.startsWith("/")) {
      event.properties.$pathname = pathname.slice(0, 256);
    } else {
      delete event.properties.$pathname;
    }
  }

  event.properties.$process_person_profile = false;
  event.properties.$geoip_disable = true;

  return event;
}

export function initPostHog(options: PostHogConsentOptions) {
  if (typeof window === "undefined") {
    return null;
  }

  const config = readConfig();
  if (!config) {
    return null;
  }

  if (!initialized && !options.analytics_tracking) {
    captureEnabled = false;
    replayEnabled = false;
    return null;
  }

  if (!initialized) {
    posthog.init(config.token, {
      api_host: config.host,
      autocapture: false,
      capture_pageview: "history_change",
      capture_pageleave: true,
      person_profiles: "never",
      disable_persistence: true,
      respect_dnt: true,
      disable_session_recording: !options.session_replay,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-sensitive], [data-sensitive] *",
        blockSelector:
          "[data-session-replay-block], [data-session-replay-block] *",
      },
      persistence: "memory",
      before_send: sanitizePostHogEvent,
    });

    initialized = true;
  }

  applyPostHogConsent(options);
  return posthog;
}

export function getPostHogClient() {
  if (!initialized) {
    return null;
  }
  return posthog;
}

export function getPostHogConsentSnapshot() {
  return {
    initialized,
    analytics_tracking: captureEnabled,
    session_replay: replayEnabled,
  };
}

export function resetPostHogSession() {
  if (typeof window === "undefined" || !initialized) {
    return;
  }

  posthog.stopSessionRecording?.();
  posthog.opt_out_capturing?.();
  posthog.reset?.();

  initialized = false;
  captureEnabled = false;
  replayEnabled = false;
}
